<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import OverviewReportFilters from './OverviewReportFilters.vue';
import BaixarRelatorio from './BaixarRelatorio.vue';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import CausaRaizQuebraAPI from 'dashboard/api/fandata/causaRaizQuebra';

// T-254 · A QUEBRA POR CAUSA RAIZ. Tabela própria, e não a `SummaryReports` compartilhada, por um
// motivo de substância: as outras três dimensões (agente, time, caixa) são entidades do Chatwoot
// e vêm do store do upstream. A causa raiz não é entidade: é um campo da conversa, com 203
// valores possíveis, e a categoria de cada uma vem da taxonomia do clube. Nada disso existe
// naquele store.
//
// Ver o construtor `V2::Reports::FandataCausaRaizQuebra` para o que a tela mostrava antes de
// 04/10 e por que estava errado.
const { t } = useI18n();

const sinceTs = ref(getUnixStartOfDay(subDays(new Date(), 6)));
const untilTs = ref(getUnixEndOfDay(new Date()));

const data = reactive({ linhas: [] });
const carregando = ref(false);

// Tempo em h/min, igual às outras telas do FanData. Nulo vira "—", e não zero: zero diria
// "respondeu na hora", que é o contrário de "não houve o que responder".
const fmtDur = seg => {
  if (seg === null || seg === undefined) return '—';
  if (seg < 60) return '<1min';
  if (seg < 3600) return `${Math.round(seg / 60)}min`;
  const h = Math.floor(seg / 3600);
  const m = Math.round((seg % 3600) / 60);
  return `${h}h${String(m).padStart(2, '0')}`;
};

const fmtPct = valor =>
  valor === null || valor === undefined ? '—' : `${valor}%`;

const total = computed(() =>
  (data.linhas || []).reduce((acc, l) => acc + (l.conversas || 0), 0)
);

// Cor só onde ela quer dizer algo: prazo cumprido e satisfação têm leitura boa/ruim. Volume e
// tempo não têm, e pintá-los faria a tabela parecer um painel de alarme.
const corDoSla = valor => {
  if (valor === null || valor === undefined) return 'text-n-slate-11';
  if (valor >= 90) return 'text-n-teal-11';
  if (valor >= 70) return 'text-n-amber-11';
  return 'text-n-ruby-11';
};

const corDoCsat = valor => {
  if (valor === null || valor === undefined) return 'text-n-slate-11';
  if (valor >= 70) return 'text-n-teal-11';
  if (valor >= 50) return 'text-n-amber-11';
  return 'text-n-ruby-11';
};

const fetchData = async () => {
  carregando.value = true;
  try {
    const { data: res } = await CausaRaizQuebraAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
    });
    data.linhas = res.linhas || [];
  } catch (e) {
    useAlert(t('QUEBRAS.CAUSA_RAIZ.ERRO'));
  } finally {
    carregando.value = false;
  }
};

const onFilterChange = ({ from, to }) => {
  sinceTs.value = from;
  untilTs.value = to;
  fetchData();
};

onMounted(fetchData);
</script>

<template>
  <OverviewReportFilters :disabled="carregando" @filter-change="onFilterChange" />

  <div class="flex flex-col flex-1 gap-3 mt-5 overflow-auto">
    <div class="flex items-start justify-between gap-3">
      <p class="text-sm text-n-slate-11">
        {{ t('QUEBRAS.CAUSA_RAIZ.DICA') }}
      </p>
      <BaixarRelatorio
        nome="causa-raiz"
        :dados="data"
        :since="sinceTs"
        :until="untilTs"
      />
    </div>

    <div
      class="overflow-auto border rounded-xl border-n-weak bg-n-solid-1 shadow"
    >
      <div v-if="carregando" class="px-5 py-10 text-center">
        <p class="text-sm text-n-slate-11">
          {{ t('QUEBRAS.CAUSA_RAIZ.CARREGANDO') }}
        </p>
      </div>
      <div v-else-if="!data.linhas.length" class="px-5 py-10 text-center">
        <p class="text-sm text-n-slate-11">
          {{ t('QUEBRAS.CAUSA_RAIZ.VAZIO') }}
        </p>
      </div>

      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-n-slate-11">
            <th class="px-5 py-2 font-medium text-left">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_CAUSA') }}
            </th>
            <th class="px-3 py-2 font-medium text-left">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_CATEGORIA') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_CONVERSAS') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_PRIMEIRA') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_RESOLUCAO') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_RESPOSTA') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_RESOLVIDAS') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_SLA') }}
            </th>
            <th class="px-3 py-2 font-medium text-center">
              {{ t('QUEBRAS.CAUSA_RAIZ.COL_CSAT') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="linha in data.linhas"
            :key="linha.causa"
            class="border-t border-n-weak hover:bg-n-alpha-1"
          >
            <td class="px-5 py-2 font-medium text-n-slate-12">
              {{ linha.label }}
            </td>
            <td class="px-3 py-2 capitalize text-n-slate-11">
              {{ linha.categoria || '—' }}
            </td>
            <td class="px-3 py-2 font-semibold text-center tabular-nums text-n-slate-12">
              {{ linha.conversas }}
            </td>
            <td class="px-3 py-2 text-center tabular-nums text-n-slate-11">
              {{ fmtDur(linha.primeira_resposta) }}
            </td>
            <td class="px-3 py-2 text-center tabular-nums text-n-slate-11">
              {{ fmtDur(linha.resolucao) }}
            </td>
            <td class="px-3 py-2 text-center tabular-nums text-n-slate-11">
              {{ fmtDur(linha.resposta) }}
            </td>
            <td class="px-3 py-2 text-center tabular-nums text-n-slate-11">
              {{ linha.resolvidas }}
            </td>
            <td
              class="px-3 py-2 font-medium text-center tabular-nums"
              :class="corDoSla(linha.sla_no_prazo)"
            >
              {{ fmtPct(linha.sla_no_prazo) }}
            </td>
            <td
              class="px-3 py-2 font-medium text-center tabular-nums"
              :class="corDoCsat(linha.csat)"
            >
              {{ fmtPct(linha.csat) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-if="data.linhas.length" class="text-xs text-n-slate-11">
      {{ t('QUEBRAS.CAUSA_RAIZ.TOTAL', { total, causas: data.linhas.length }) }}
    </p>
  </div>
</template>
